import { useTranslation } from "react-i18next"

import { AlertCircle, Bell, ChevronRight } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useAdminRecentAlertsQuery } from "@/features/admin-dashboard/hooks/admin-queries"

type Props = { onOpen: () => void }

/** Compact read-only entry point; incident triage stays in Operations. */
export function AdminIncidentSummaryCard({ onOpen }: Props) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useAdminRecentAlertsQuery(7)
  const active = data?.counts.active ?? 0
  const critical = data?.severity_counts.critical ?? 0
  const warning = data?.severity_counts.warning ?? 0
  let summaryText = t("adminDashboard.alerts.noActiveIncidents", "No active incidents")
  if (isLoading) summaryText = t("common.loading", "Loading…")
  else if (isError || data?.error || !data)
    summaryText = t("adminDashboard.alerts.summaryUnavailable", "Incident status unavailable")
  else if (active > 0) {
    summaryText = t(
      "adminDashboard.alerts.summaryActive",
      "{{count}} active · {{critical}} critical · {{warning}} warning",
      { count: active, critical, warning }
    )
  }

  return (
    <Card className="rounded-lg shadow-none">
      <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-md border">
            {active > 0 ? (
              <AlertCircle aria-hidden="true" className="size-5" />
            ) : (
              <Bell aria-hidden="true" className="size-5" />
            )}
          </span>
          <div>
            <h2 className="text-lg font-semibold">
              {t("adminDashboard.alerts.recentIncidents", "Recent Incidents")}
            </h2>
            <p className="text-muted-foreground text-sm">{summaryText}</p>
          </div>
        </div>
        <Button className="min-h-11" type="button" variant="outline" onClick={onOpen}>
          {t("adminDashboard.alerts.openIncidents", "View incidents")}
          <ChevronRight aria-hidden="true" className="ml-2 size-4" />
        </Button>
      </CardContent>
    </Card>
  )
}
